import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import api from "../api";
import { useNavigate } from "react-router-dom";
import CustomTextInput from "../components/CustomTextInput";
import Layout from "../components/Layout";
import ConfirmDeletePopup from "../components/ConfirmDeletePopup";
import userIcon from "../assets/userIcon.svg";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Spinner from "../components/Spinner";
import { FaEdit } from "react-icons/fa";
import CustomPhoneInput from "../components/CustomPhoneInput";

export default function ClientDetailsPage() {
  const navigate = useNavigate();
  const { id } = useParams(); // Obtener el ID del cliente desde la URL
  const [client, setClient] = useState(null);
  const [name, setName] = useState("");
  const [isCorporate, setIsCorporate] = useState("");
  const [ruc, setRUC] = useState("");
  const [email, setEmail] = useState("");
  const [contact, setContact] = useState("");
  const [logo, setLogo] = useState(null); // Imagen del logo del cliente
  const [previewLogo, setPreviewLogo] = useState(null); // Vista previa de la imagen cargada
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  const [isPopupOpen, setIsPopupOpen] = useState(false);

  const isCorporateOptions = [
    { value: true, label: "Corporativo" },
    { value: false, label: "Particular" },
  ];

  useEffect(() => {
    async function fetchClient() {
      try {
        const response = await api.clients.getOne(id);
        setClient(response);
        setName(response.name);
        setIsCorporate(response.isCorporate);
        setRUC(response.ruc);
        setEmail(response.email);
        setContact(response.phone);
        setLogo(response.logo); // Guardar la URL del logo actual
        setError(""); // Limpiar el mensaje de error en caso de éxito
      } catch (error) {
        console.error("Error fetching client details:", error);
        setError("Error al obtener los detalles del cliente."); // Mensaje de error
      }
    }
    fetchClient();
  }, [id]);

  const handleDeleteClick = () => {
    setIsPopupOpen(true); // Abre el popup
  };

  const handleDeleteConfirm = async () => {
    try {
      await api.clients.delete(id);
      console.log("Cliente eliminado");
      navigate(`/clients`);
      setError(""); // Limpiar el mensaje de error en caso de éxito
    } catch (error) {
      console.error("Error deleting client:", error);
      setError("Error al eliminar el cliente."); // Mensaje de error
    }
    setIsPopupOpen(false); // Cierra el popup después de la confirmación
  };

  const handlePopupClose = () => {
    setIsPopupOpen(false); // Cierra el popup sin eliminar
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogo(file);
      setPreviewLogo(URL.createObjectURL(file)); // Mostrar vista previa de la imagen cargada
      setError(""); // Limpiar el mensaje de error
    }
  };

  const handleSave = async () => {
    setFieldErrors({}); // Limpiar errores de campo anteriores
    try {
      const formData = new FormData();
      formData.append("name", name);
      formData.append("isCorporate", isCorporate);
      formData.append("ruc", ruc);
      formData.append("email", email);
      formData.append("phone", contact);
      if (logo !== client.logo) {
        formData.append("logo", logo);
      }

      await api.clients.update(id, formData);
      setClient({
        ...client,
        name,
        ruc,
        email,
        phone: contact,
        logo: previewLogo || client.logo,
      });
      toast.success("Datos guardados con éxito");
      setError("");
    } catch (error) {
      if (error.message) {
        setFieldErrors(error.message);
      } else {
        setError(
          "Error al actualizar los datos del cliente. Intente nuevamente."
        );
      }
    }
  };

  const [opacityLevel, setOpacityLevel] = useState(0);

  useEffect(() => {
    if (!client) {
      let intervalId = setInterval(() => {
        setOpacityLevel((prev) => Math.min(prev + 0.1, 1)); // Incrementa la opacidad hasta 1
      }, 500); // Ajusta el intervalo para controlar la velocidad
      return () => clearInterval(intervalId); // Limpia el intervalo al desmontar
    } else {
      setOpacityLevel(1); // Asegúrate de que la opacidad esté al máximo al terminar de cargar
    }
  }, [client]);

  if (!client) {
    return <Spinner />;
  }

  return (
    <Layout>
      <div
        className="poppins-medium flex flex-col h-screen bg-[#F8F9FB] w-full md:p-8 transition-opacity ease-in-out"
        style={{
          opacity: opacityLevel,
          transition: "opacity 0.5s linear",
        }}
      >
        <h1 className="poppins-bold text-lg text-left text-[#1F384C] mb-6">
          Detalles del cliente
        </h1>
        <div className="p-8 border rounded-2xl">
          <ToastContainer />
          {/* Mostrar mensaje de error si existe */}
          {error && <p className="text-red-500 text-sm mb-4">{error}</p>}

          <div className="flex flex-col items-center mb-10">
            <div className="relative w-40 h-40 rounded-full  mb-4">
              <img
                src={previewLogo || client.logo || userIcon} // Mostrar la vista previa o el logo actual
                alt={client.name}
                className="w-full h-full object-cover"
              />
              {/* Ícono de edición */}
              <button
                className="absolute top-2 right-2 bg-white p-2 rounded-full shadow-lg hover:bg-gray-200 transition"
                onClick={() => document.getElementById("logoInput").click()} // Activar input de archivo al hacer clic
              >
                <FaEdit className="text-gray-700 text-lg" />
              </button>
              {/* Input de archivo oculto */}
              <input
                type="file"
                id="logoInput"
                accept="image/*"
                onChange={handleLogoChange}
                className="hidden"
              />
              {fieldErrors.logo && (
                <p className="text-red-600 text-sm">{fieldErrors.logo[0]}</p>
              )}
            </div>

            <h2 className="flex flex-col justify-between items-center gap-2 text-xl">
              <div className="h-[38px] px-6 bg-[#92C8FF] border border-[#0080FF] text-sm rounded-3xl flex justify-center items-center">
                Cliente
              </div>
              <div className="bg-white p-3 rounded">
                <CustomTextInput
                  label="Nombre del cliente"
                  placeholder="Ingresa el nombre del cliente"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
                {/* Muestra error específico para el campo "name" */}
                {fieldErrors.name && (
                  <p className="text-red-600 text-sm">{fieldErrors.name[0]}</p>
                )}
              </div>
            </h2>
          </div>

          <div className="bg-white p-6 pb-12 rounded-lg shadow-md mb-6">
            <h3 className="text-xl mb-6 text-[#595A5C]">
              Información del cliente
            </h3>

            <div className="flex mx-5 gap-x-32 gap-y-10 flex-wrap">
              <div className="flex flex-col items-start gap-2">
                <>
                  <CustomTextInput
                    label="RUC"
                    placeholder="Escribe el RUC del cliente"
                    type="text"
                    value={ruc}
                    onChange={(e) => setRUC(e.target.value)}
                  />
                  {fieldErrors.ruc && (
                    <p className="text-red-600 text-sm">{fieldErrors.ruc[0]}</p>
                  )}
                </>
              </div>

              <div className="flex flex-col items-start gap-2">
                <>
                  <CustomTextInput
                    label="Correo"
                    placeholder="Ingresa el correo electrónico"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                  {fieldErrors.email && (
                    <p className="text-red-600 text-sm">
                      {fieldErrors.email[0]}
                    </p>
                  )}
                </>
              </div>

              <div className="flex flex-col items-start gap-2">
                <>
                  <CustomPhoneInput
                    label="Teléfono"
                    placeholder="Escribe el número de contacto"
                    value={contact}
                    onChange={(phone) => setContact(phone)}
                  />
                  {fieldErrors.phone && (
                    <p className="text-red-600 text-sm">
                      {fieldErrors.phone[0]}
                    </p>
                  )}
                </>
              </div>

              <div className="flex flex-col items-start gap-2">
                <>
                  <CustomTextInput
                    label="Tipo de cliente"
                    placeholder="Selecciona el tipo de cliente"
                    type="select"
                    value={isCorporate}
                    onChange={(e) => setIsCorporate(e.target.value)}
                    options={isCorporateOptions}
                  />
                  {fieldErrors.isCorporate && (
                    <p className="text-red-600 text-sm">
                      {fieldErrors.isCorporate[0]}
                    </p>
                  )}
                </>
              </div>
            </div>
          </div>

          <div className="md:space-x-4 space-x-3 flex justify-between">
            <button
              onClick={handleSave}
              className="bg-[#59A8D4] text-white text-xs py-4 px-4 rounded-2xl tracking-wider"
            >
              Guardar
            </button>
            <button
              onClick={handleDeleteClick}
              className="bg-[#BE202F] text-white text-xs py-4 px-4 rounded-2xl tracking-wider"
            >
              Eliminar
            </button>
          </div>
        </div>
        <ConfirmDeletePopup
          isOpen={isPopupOpen}
          onClose={handlePopupClose}
          onConfirm={handleDeleteConfirm}
          itemName={client.name}
        />
      </div>
    </Layout>
  );
}
